<template>
  <div
    ref="sliderRef"
    class="w-full flex-1 glass-frosted rounded-8xl overflow-hidden cursor-pointer relative"
    @mousedown="startDrag"
  >
    <div
      class="bg-white h-full rounded-8xl"
      :style="{
        width: `${widthPercent}%`,
      }"
    ></div>
    <div class="absolute p-5 inset-0 flex items-center pointer-events-none">
      <Icon icon="Status-Wireframe-Day" class="size-10" />
      <div class="flex flex-col gap-1 ml-2">
        <div class="text-3xl font-bold">{{ displayValue }}</div>
        <div class="text-m-medium">{{ $t('lighting.brightness') }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { Icon } from '@rapoo/element'
  import { debounce } from '@/utils/optimize'
  import { useSliderDrag } from '@/composables/useSliderDrag'

  const props = withDefaults(
    defineProps<{
      modelValue: number
      /** 亮度最大值（默认 100 连续调节；5 档设备如 GCOME_AP 传 100）。 */
      max?: number
      /** 步进（连续调节传 1；5 档设备如 GCOME_AP 传 25，即 0/25/50/75/100）。 */
      step?: number
    }>(),
    {
      modelValue: 60,
      max: 100,
      step: 1,
    },
  )
  const emit = defineEmits(['update:modelValue', 'change'])

  // 拖拽实现
  const sliderRef = ref<HTMLElement | null>(null)

  const widthPercent = computed(() =>
    Math.min(100, Math.max(0, (props.modelValue / props.max) * 100)),
  )

  /** 连续模式（0-100 步进 1）显示百分比；5 档模式（如 GCOME_AP 0-100 步进 25）显示档位数值 0/25/50/75/100。 */
  const displayValue = computed(() =>
    props.max === 100 && props.step === 1 ? `${props.modelValue} %` : String(props.modelValue),
  )

  /** 把拖动百分比（0-100）量化为 max/step 下的档位值。 */
  const quantize = (percentage: number) => {
    const raw = (percentage / 100) * props.max
    const stepped = Math.round(raw / props.step) * props.step
    return Math.min(props.max, Math.max(0, stepped))
  }

  const { startDrag } = useSliderDrag(sliderRef, {
    onChange(percentage) {
      emit('update:modelValue', quantize(percentage))
    },
    onEnd(percentage) {
      emit('change', quantize(percentage))
    },
  })

  // 快捷键实现
  import { useCombinationKey } from '@/composables/useCombinationKey'
  import { useActiveState } from '@/composables/useComponentsState'
  useActiveState(sliderRef, {
    autoListen: true,
    onBlur: () => {
      unregisterShortcuts()
    },
    onFocus: () => {
      registerShortcuts()
    },
  })
  const increaseBrightness = (step: number = props.step) => {
    const newValue = Math.min(props.max, props.modelValue + step)
    emit('update:modelValue', newValue)
  }
  const decreaseBrightness = (step: number = props.step) => {
    const newValue = Math.max(0, props.modelValue - step)
    emit('update:modelValue', newValue)
  }
  let shortcutHandlers: { unregister: () => void }[] = []
  const registerShortcuts = () => {
    const { unregister: unregisterRight } = useCombinationKey(
      'arrowright',
      debounce((e: KeyboardEvent) => {
        e.preventDefault()
        increaseBrightness(props.step)
      }, 100),
      { continuous: true },
    )
    const { unregister: unregisterLeft } = useCombinationKey(
      'arrowleft',
      debounce((e: KeyboardEvent) => {
        e.preventDefault()
        decreaseBrightness(props.step)
      }, 100),
      { continuous: true },
    )
    shortcutHandlers = [{ unregister: unregisterRight }, { unregister: unregisterLeft }]
  }

  const unregisterShortcuts = () => {
    shortcutHandlers.forEach((handler) => {
      handler.unregister()
    })
    shortcutHandlers = []
  }
</script>
